import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Avatar,
  Container,
  Alert,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { login } from "../slices/auth";
import { clearMessage } from "../slices/message";

const StyledCard = styled(Card)(({ theme }) => ({
  maxWidth: 400,
  margin: "auto",
  marginTop: theme.spacing(8),
  padding: theme.spacing(2),
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  margin: "auto",
  marginBottom: theme.spacing(2),
  width: theme.spacing(8),
  height: theme.spacing(8),
}));

const Login = (props) => {
  const [loading, setLoading] = useState(false);
  const { isLoggedIn } = useSelector((state) => state.auth);
  const { message } = useSelector((state) => state.message);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  const initialValues = {
    username: "",
    password: "",
    otp: "",
  };

  const validationSchema = Yup.object().shape({
    username: Yup.string().required("This field is required!"),
    password: Yup.string().required("This field is required!"),
    otp: Yup.string()
      .required("Authy Code is required!")
      .matches(/^\d{6,7}$/, "Authy Code must be 6-7 digits"),
  });

  const handleLogin = (formValue) => {
    const { username, password, otp } = formValue;
    setLoading(true);

    dispatch(login({ username, password, otp }))
      .unwrap()
      .then(() => {
        props.history.push("/");
      })
      .catch(() => {
        setLoading(false);
      });
  };

  if (isLoggedIn) {
    return <Redirect to="/profile" />;
  }

  return (
    <Container component="main" maxWidth="xs">
      <StyledCard>
        <CardContent>
          <Box display="flex" flexDirection="column" alignItems="center">
            <StyledAvatar
              src="//ssl.gstatic.com/accounts/ui/avatar_2x.png"
              alt="profile-img"
            />
            <Typography component="h1" variant="h5" gutterBottom>
              Sign In
            </Typography>
            
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleLogin}
            >
              {({ values, errors, touched, handleChange, handleBlur }) => (
                <Form style={{ width: "100%" }}>
                  <Box mt={2}>
                    <TextField
                      fullWidth
                      id="username"
                      name="username"
                      label="Username"
                      value={values.username}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.username && Boolean(errors.username)}
                      helperText={touched.username && errors.username}
                      margin="normal"
                      variant="outlined"
                    />
                  </Box>

                  <Box mt={2}>
                    <TextField
                      fullWidth
                      id="password"
                      name="password"
                      label="Password"
                      type="password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      margin="normal"
                      variant="outlined"
                    />
                  </Box>

                  <Box mt={2}>
                    <TextField
                      fullWidth
                      id="otp"
                      name="otp"
                      label="Authy Code"
                      value={values.otp}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.otp && Boolean(errors.otp)}
                      helperText={touched.otp && errors.otp}
                      margin="normal"
                      variant="outlined"
                      placeholder="Enter 7 digit code"
                    />
                  </Box>

                  <Box mt={3}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      sx={{
                        backgroundColor: "#4caf50",
                        "&:hover": {
                          backgroundColor: "#45a049",
                        },
                        py: 1.5,
                      }}
                    >
                      {loading && (
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                      )}
                      Sign In
                    </Button>
                  </Box>
                </Form>
              )}
            </Formik>

            {message && (
              <Box mt={2} width="100%">
                <Alert severity="error">{message}</Alert>
              </Box>
            )}
          </Box>
        </CardContent>
      </StyledCard>
    </Container>
  );
};

export default Login;
